import { logger } from '@adornis/base/logging.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { css } from '@adornis/chemistry/directives/css.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { askForFile } from '@adornis/file-utils/client/helper-methods.js';
import { AdornisFile } from '@adornis/file-utils/db/files.js';
import { FormController } from '@adornis/forms/x-form-controller.js';
import { MultipleValidationError } from '@adornis/validation/errors/MultipleValidationError.js';
import { Bundesland } from '@chancenwerk/db/enums/enums.js';
import type { LogistikProduct } from '@chancenwerk/db/LogistikProduct.js';
import { Role } from '@chancenwerk/db/semantics/Role.js';
import { ChromiumDialog } from '@chancenwerk/design/components/cr-dialog.js';
import type { ChromiumImage } from '@chancenwerk/design/components/cr-image.js';
import { ChromiumSnackbar } from '@chancenwerk/design/components/cr-snackbar.js';
import { html, nothing, type PropertyValueMap } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { repeat } from 'lit/directives/repeat.js';
import { firstValueFrom, map, of, switchMap, timer } from 'rxjs';
import { LogistikInfoMail } from '../../../../../src/db/LogistikInfoMail.js';
import { g_getProductCategoryString } from '../../../../../src/logistik/client/helper.js';
import '../logistik-component-tinymce.js';

/**
 * @customElement logistik-dialog-info-mail
 */
@customElement('logistik-dialog-info-mail')
export class LogistikDialogInfoMail extends ChromiumDialog<boolean> {
  static override get element_name(): string {
    return 'logistik-dialog-info-mail';
  }

  @property() products: LogistikProduct[] = [];

  @state() private readonly _form = new FormController(
    this,
    new LogistikInfoMail({ productIDs: this.products.map(prod => prod._id) }),
    {
      onSuccess: async entity => {
        if (!this._file) return;
        const fileID = await AdornisFile.upload(this._file);
        entity.bannerFileID = fileID;
      },
      error: err => {
        logger.error({ err }, 'Es gab ein Problem beim validieren der Info Mail');
        if (err instanceof MultipleValidationError)
          return ChromiumSnackbar.show('Es gab ein Problem beim Validieren: ' + err.validationErrors[0]?.message);
        return ChromiumSnackbar.show('Es gab ein Problem beim Validieren: ' + err.message);
      },
    },
  );

  @state() private readonly _recipients = new RXController(this, this._form.document!.whenChanging, doc$ =>
    doc$.pipe(
      switchMap(doc => {
        if (!doc.userRoles.length) return of([]);
        return LogistikInfoMail.getApplicableEmailAddresses(doc.userRoles, doc.states)();
      }),
    ),
  );
  @state() private readonly _numRecipients = new RXController(this, this._recipients.observable, recipients$ =>
    recipients$.pipe(map(recipients => recipients.length || 0)),
  );

  @state() private _file?: File;

  override heading = 'Info-Mail für ausgewähte Produkte versenden';

  protected override willUpdate(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): void {
    super.willUpdate(_changedProperties);

    if (this._form.document && this._form.document.productIDs.length !== this.products.length)
      this._form.document.productIDs = this.products.map(prod => prod._id);
  }

  override content() {
    return html`
      <x-flex space="lg">
        <x-flex space="md">
          <cr-dropdown-multiselection
            placeholder="An wen soll die Mail gehen?"
            .selectables=${[Role.LEKO, Role.SCHUKO, Role.PROGRAM_MANAGEMENT]}
            .renderString=${(role?: Role) =>
              ({
                [Role.LEKO]: 'Alle Lernkoordinatoren',
                [Role.SCHUKO]: 'Alle Schulkoordinatoren',
                [Role.PROGRAM_MANAGEMENT]: 'Alle Programmleiter',
              }[role ?? ''] ?? '')}
            ${this._form.field('userRoles')}
          ></cr-dropdown-multiselection>
          <cr-dropdown-multiselection
            placeholder="Nur an bestimmte Bundesländer?"
            .selectables=${Object.values(Bundesland)}
            ${this._form.field('states')}
          ></cr-dropdown-multiselection>
          <cr-input placeholder="Betreff" ${this._form.field('subject')}></cr-input>
          <x-hr></x-hr>
          ${this._file
            ? html`
                <cr-image
                  mode="width"
                  .imageToUpload=${this._file}
                  ${ref(crImage => {
                    if (!crImage) return;
                    // @ts-expect-error this is internal but we need it // TODO reevaluate
                    (crImage as ChromiumImage).handleSelectedFile(this._file);
                  })}
                ></cr-image>
              `
            : nothing}
          <x-flex horizontal space="sm">
            <cr-button
              flex
              mode="outline"
              @click=${async () => {
                const fileList = await askForFile({ acceptFileTypes: '.png,.jpg,.jpeg' });
                if (!fileList?.length) return;
                this._file = fileList[0];
                this._form.document!.bannerFileID = null;
              }}
            >
              Banner ${this._file ? 'ändern' : 'hochladen'}
            </cr-button>
            ${this._file
              ? html`
                  <cr-button
                    mode="outline"
                    icon="delete"
                    tone="error"
                    @click=${() => (this._file = undefined)}
                  ></cr-button>
                `
              : nothing}
          </x-flex>
          <logistik-component-tinymce placeholder="Einleitungstext" ${this._form.field('introText')}>
          </logistik-component-tinymce>
          <x-hr></x-hr>
          ${this._productList()}
          <x-hr></x-hr>
          <logistik-component-tinymce placeholder="Abschließende Worte" ${this._form.field('outroText')}>
          </logistik-component-tinymce>
        </x-flex>
        <x-hr></x-hr>
        <x-flex horizontal space="lg" space-between>
          <cr-text tone="subtle"> Die Mail wird versendet an ${this._numRecipients.value ?? 0} Adressaten </cr-text>
          <cr-icon ${css({ cursor: 'pointer' })} @click=${() => this._showMailAddresses()}> help_outline </cr-icon>
        </x-flex>
        <x-flex horizontal end crossaxis-center space="sm">
          <cr-button mode="outline" icon="bug_report" @click=${() => this._sendTestMail()}>
            Vorschau E-Mail an meine Mail-Adresse schicken
          </cr-button>
        </x-flex>
      </x-flex>
    `;
  }

  override footerContent() {
    return html`
      <x-flex horizontal end space="md">
        <cr-button mode="outline" @click=${() => this.close(false)}> Abbrechen </cr-button>
        <cr-button @click=${() => this._sendMails()}> Mails versenden </cr-button>
      </x-flex>
    `;
  }

  private _productList() {
    return html`
      <x-flex space="sm">
        <x-text> Ausgewählte Produkte (${this.products.length}) </x-text>
        ${repeat(
          this.products,
          (product, i) =>
            html`
              <x-basic-card horizontal>
                <cr-image
                  disabled
                  mode="cover"
                  .value="${product.imageID}"
                  ${css({ width: '75px', aspectRatio: '1 / 1' })}
                  flex-none
                >
                </cr-image>
                <x-flex space="sm" padding="sm" flex center>
                  <x-text bold> ${product.title} </x-text>
                  <x-text> ${g_getProductCategoryString(product._class)} </x-text>
                </x-flex>
                <x-flex
                  center
                  crossaxis-center
                  padding="md"
                  ${css({ background: this.colors.tone.error, color: this.colors.on.tone.error, cursor: 'pointer' })}
                  @click=${() => {
                    this.products.splice(i, 1);
                    this.requestUpdate();
                  }}
                >
                  <x-icon> delete </x-icon>
                </x-flex>
              </x-basic-card>
            `,
        )}
      </x-flex>
    `;
  }

  private async _sendTestMail() {
    const infoMail = await this._form.validate();
    if (!infoMail) return;

    await ChromiumDialog.waitFor(
      Promise.all([firstValueFrom(timer(1000)), LogistikInfoMail.sendInfoTestMail(infoMail)()]),
    );

    void XSnackbar.show('Vorschau wurde versandt');
  }

  private async _sendMails() {
    const infoMail = await this._form.validate();
    if (!infoMail) return;

    if (
      !this._numRecipients.value ||
      !(await ChromiumDialog.confirm(
        `Wirklich Info-Mail zu den ausgewählten Prdukten an ${this._numRecipients.value} Adressaten schicken?`,
      ))
    )
      return;

    await ChromiumDialog.waitFor(Promise.all([firstValueFrom(timer(1000)), LogistikInfoMail.sendInfoMail(infoMail)()]));

    void XSnackbar.show('Mails wurden versandt');
    await this.close(true);
  }

  private _showMailAddresses() {
    return ChromiumDialog.showPopup({
      content: resolve =>
        html`
          <x-flex space="lg" padding="md">
            <cr-h4> Ausgewählte Mail-Adressen </cr-h4>
            <x-flex space="sm">
              ${repeat(this._recipients.value ?? [], recipient => html` <cr-text> ${recipient} </cr-text> `)}
            </x-flex>
          </x-flex>
        `,
    });
  }
}
